import React, { useEffect, useState } from "react";
import { Box, IconButton, Switch, Tooltip, Typography } from "@mui/material";
import InfoIcon from '@mui/icons-material/Info';
import { styled, useTheme } from "@mui/material/styles";
import { useLocation } from "react-router-dom";
import { getIsV2, setIsV2 } from "utils/versionToggle";

const AntSwitch = styled(Switch)(({ theme }) => ({
  width: "34px",
  height: "20px",
  padding: 0,
  display: "flex",
  position: "relative",
  top: "7px",
  "& .MuiSwitch-switchBase": {
    padding: "2px",
    transitionDuration: "200ms",
    "&.Mui-checked": {
      transform: "translateX(14px)",
      color: theme.palette.toggle.thumb,
      "& + .MuiSwitch-track": {
        backgroundColor: theme.palette.toggle.trackOn,
        opacity: 1,
        border: "none",
      },
    },
    "&:hover": {
      backgroundColor: "transparent",
    },
    "&.Mui-active .MuiSwitch-thumb": {
      transform: "scale(0.96)",
    },
    "&.Mui-disabled": {
      color: theme.palette.toggle.thumb,
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: 0.5,
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 16,
    height: 16,
    backgroundColor: theme.palette.toggle.thumb,
    boxShadow: theme.palette.toggle.thumbShadow,
    transition: "all 200ms ease",
  },
  "& .MuiSwitch-track": {
    borderRadius: 999,
    backgroundColor: theme.palette.toggle.trackOff,
    opacity: 1,
    transition: "background-color 200ms ease",
  },
}));

interface VersionToggleProps {
  value?: 'V1' | 'V2';
  onChange?: (version: 'V1' | 'V2') => void;
}

const VersionToggle: React.FC<VersionToggleProps> = ({ 
  value, 
  onChange
}) => {
  const location = useLocation();
  const theme = useTheme();
  const colors = theme.palette;
  // If value prop is provided, use it; otherwise use the existing session storage logic
  const isControlled = value !== undefined;
  const [isV2State, setIsV2State] = useState(isControlled ? (value === 'V2') : getIsV2());

  const toggleVersion = () => {
    setIsV2State((prev) => {
      const newValue = !prev;

      // If controlled by parent component (has onChange), call callback only
      if (isControlled && onChange) {
        onChange(newValue ? 'V2' : 'V1');
        return newValue;
      }

      // Otherwise, use existing global behavior (sessionStorage + reload)
      setIsV2(!prev);
      setTimeout(() => globalThis.location.reload(), 100);
      return newValue;
    });
  };

  // Only update sessionStorage if NOT controlled by parent (preserve existing behavior)
  useEffect(() => {
    if (!isControlled) setIsV2(isV2State); // update sessionStorage whenever isV2State changes
  }, [isV2State, isControlled]);

  useEffect(() => {
    if (!isControlled && location.pathname === "/data-pipeline/dataOps" && isV2State) {
      document.body.classList.add("v2Background");
    } else {
      document.body.classList.remove("v2Background");
    }
    return () => {
      document.body.classList.remove("v2Background");
    };
  }, [isV2State, location.pathname, isControlled]);

  //Syncs V2/V1 state across tabs and events
  useEffect(() => {
    const sync = () => setIsV2State(getIsV2());
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'isV2') sync();
    };
    globalThis.addEventListener('isV2Change', sync);
    globalThis.addEventListener('storage', onStorage);
    return () => {
      globalThis.removeEventListener('isV2Change', sync);
      globalThis.removeEventListener('storage', onStorage);
    };
  }, []);

  return (
    <div className="flex justify-end items-center p-4 rounded-lg shadow-md"
      style={{
        // position: "fixed",
        // top: "27px",
        // right: "90px",
        zIndex: 1000,
      }}>
      <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Tooltip
            arrow
            className="toolTip"
            title={
            <Box
              component="ul"
              sx={{
                paddingLeft: "16px",
                margin: 0,
                py: 1,
                pr: 1,
                backgroundColor: colors.tooltip.background.primary,
              }}
            >
              <li><Typography variant="body2" sx={{ color: colors.tooltip.text.title }}>Control-M</Typography></li>
              <li><Typography variant="body2" sx={{ color: colors.tooltip.text.title }}>HWA</Typography></li>
              <li><Typography variant="body2" sx={{ color: colors.tooltip.text.title }}>Teradata</Typography></li>
              <li><Typography variant="body2" sx={{ color: colors.tooltip.text.title }}>DDH</Typography></li>
              <li><Typography variant="body2" sx={{ color: colors.tooltip.text.title }}>MDT</Typography></li>
            </Box>
          }
          slotProps={{
            tooltip: {
              sx: {
                p: 0,
                maxWidth: { xs: 260, sm: 320, md: 360 },
                bgcolor: colors.tooltip.background.primary,
                color: colors.tooltip.text.primary,
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: `
                  -6px 6px 12px rgba(0, 0, 0, 0.18),
                  -2px 10px 8px rgba(0, 0, 0, 0.18)
                `,
              },
            },
            arrow: {
              sx: {
                color: colors.tooltip.background.primary,
              },
            },
          }}
          >
            <IconButton size="small" sx={{ color: "grey", padding: "4px" }}>
              <InfoIcon sx={{color: 'rgb(113, 117, 143)'}}/>
            </IconButton>
          </Tooltip>
      </span>
     
      <Box component="span" className=" font-semibold" sx={{ color: isControlled ? `${colors.textLight.light} !important` : `${colors.textLight.main} !important`, display: "flex", alignItems: "center", height: "18px", position: "relative", top: "7px", fontWeight: 600 }}>V1</Box>
      <AntSwitch checked={isV2State} onChange={toggleVersion} sx={{ mx: 2 }}/>
      <Box component="span" className=" font-semibold" sx={{ color: isControlled ? `${colors.textLight.light} !important` : `${colors.textLight.main} !important`, display: "flex", alignItems: "center", height: "18px", position: "relative", top: "7px", fontWeight: 600 }}>V2</Box>

      <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Tooltip
            arrow
            className="toolTip"
            title={
            <Box
              component="ul"
              sx={{
                paddingLeft: "16px",
                margin: 0,
                py: 1,
                pr: 1,
                backgroundColor: colors.tooltip.background.primary,
              }}
            >
              <li><Typography variant="body2" sx={{ color: colors.tooltip.text.title }}>ADF</Typography></li>
              <li><Typography variant="body2" sx={{ color: colors.tooltip.text.title }}>Windows Server</Typography></li>
              <li><Typography variant="body2" sx={{ color: colors.tooltip.text.title }}>SAP BW</Typography></li>
              <li><Typography variant="body2" sx={{ color: colors.tooltip.text.title }}>SQL Server Agent</Typography></li>
              <li><Typography variant="body2" sx={{ color: colors.tooltip.text.title }}>DDH</Typography></li>
              <li><Typography variant="body2" sx={{ color: colors.tooltip.text.title }}>MDT</Typography></li>
              <li><Typography variant="body2" sx={{ color: colors.tooltip.text.title }}>ADB</Typography></li>
              <li><Typography variant="body2" sx={{ color: colors.tooltip.text.title }}>Teradata</Typography></li>
              <li><Typography variant="body2" sx={{ color: colors.tooltip.text.title }}>BODS</Typography></li>
            </Box>
          }
          slotProps={{
            tooltip: {
              sx: {
                p: 0,
                maxWidth: { xs: 260, sm: 320, md: 360 },
                bgcolor: colors.tooltip.background.primary,
                color: colors.tooltip.text.primary,
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: `
                  -6px 6px 12px rgba(0, 0, 0, 0.18),
                  -2px 10px 8px rgba(0, 0, 0, 0.18)
                `,
              },
            },
            arrow: {
              sx: {
                color: colors.tooltip.background.primary,
              },
            },
          }}
          >
            <IconButton size="small" sx={{ color: "grey", padding: "4px" }}>
              <InfoIcon sx={{color: 'rgb(113, 117, 143)'}}/>
            </IconButton>
          </Tooltip>
      </span>
    </div>
  );
};

export default VersionToggle;